import * as ed from '@noble/ed25519';
import { sha256 } from '@noble/hashes/sha256';
import { ripemd160 } from '@noble/hashes/ripemd160';
import { sha512 } from '@noble/hashes/sha2.js';

const XRPL_B58_ALPHABET =
  'rpshnaf39wBUDNEGHJKLM4PQRST7VWXYZ2bcdeCg65jkm8oFqi1tuvAxyz';
const HEX_RE = /^[0-9a-f]{64}$/i;
const ED25519_PREFIX = 0xed;

ed.hashes.sha512 = sha512;

export function hash160(bytes) {
  return ripemd160(sha256(bytes));
}

function checksum(payload) {
  return sha256(sha256(payload)).slice(0, 4);
}

function encodeBase58Xrpl(bytes) {
  let value = 0n;
  for (const byte of bytes) {
    value = (value << 8n) | BigInt(byte);
  }

  let out = '';
  while (value > 0n) {
    const mod = Number(value % 58n);
    out = XRPL_B58_ALPHABET[mod] + out;
    value /= 58n;
  }

  let leadingZeroes = 0;
  while (leadingZeroes < bytes.length && bytes[leadingZeroes] === 0) {
    leadingZeroes += 1;
  }

  return XRPL_B58_ALPHABET[0].repeat(leadingZeroes) + (out || '');
}

export function accountIdToAddress(accountId) {
  const payload = new Uint8Array(1 + accountId.length);
  payload[0] = 0x00;
  payload.set(accountId, 1);

  const encoded = new Uint8Array(payload.length + 4);
  encoded.set(payload, 0);
  encoded.set(checksum(payload), payload.length);
  return encodeBase58Xrpl(encoded);
}

export function publicKeyToAddress(publicKey) {
  return accountIdToAddress(hash160(publicKey));
}

export function randomPrivKey() {
  return ed.utils.randomSecretKey();
}

export function bytesToHex(bytes) {
  let s = '';
  for (let i = 0; i < bytes.length; i++) {
    s += bytes[i].toString(16).padStart(2, '0');
  }
  return s;
}

export function hexToBytes(hex) {
  if (hex.length % 2) throw new Error('odd hex');
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

export function deriveAll(privKey) {
  const rawPublicKey = ed.getPublicKey(privKey);
  const publicKey = new Uint8Array(1 + rawPublicKey.length);
  publicKey[0] = ED25519_PREFIX;
  publicKey.set(rawPublicKey, 1);
  return {
    privKey,
    privateKeyHex: bytesToHex(privKey),
    publicKey,
    publicKeyHex: bytesToHex(publicKey),
    classicAddress: publicKeyToAddress(publicKey),
  };
}

export function parsePrivKey(input) {
  const normalized = input.trim().replace(/^0x/i, '');
  if (!HEX_RE.test(normalized)) {
    throw new Error('Expected exactly 64 hexadecimal characters.');
  }
  return {
    privKey: hexToBytes(normalized.toLowerCase()),
    format: 'hex',
  };
}
